<template>
    <div class="b2b-card">
        <div class="header">
            <h2 class="title-l">Shipping Information</h2>
            <Link v-if="allowEdit" :href="route('checkout.shipping')" class="text-rothcoWhite">Edit</Link>
        </div>
        <div class="content">
            <div class="row">
                <div class="title-s title">
                    Address
                </div>
                <div class="text-rothcoText">
                    <div class="paragraph-default pb-75">
                        {{ shipping.shipping_name }}
                    </div>
                    <div v-if="shipping.shipping_in_care_of" class="paragraph-default pb-75">
                        In Care Of: {{ shipping.shipping_in_care_of }}
                    </div>
                    <div class="paragraph-default pb-75">
                        {{ shipping.shipping_address }}, {{ shipping.shipping_address_2 }}
                        {{ shipping.shipping_city }}, 
                        {{ shipping.shipping_state }} {{ shipping.shipping_zip }} {{ shipping.shipping_country }}
                    </div>
                    <div v-if="!d2c" class="paragraph-default pb-75">
                        Final Destination: {{ finalDestinationString }}
                    </div>
                    <div class="paragraph-default pb-25">
                        {{ shipping.shipping_phone }}
                    </div>
                    <div v-if="shipping.shipping_email && !d2c" class="paragraph-default">
                        {{ shipping.shipping_email }}
                    </div>
                </div>
            </div>
            <div class="row">
                <div class="title-s title">
                    Method
                </div>
                <div class="text-rothcoText">
                    <div v-if="serviceLevel && !d2c" class="paragraph-default pb-25">
                        {{ serviceLevel }}
                    </div>
                    <div class="paragraph-default pb-25">
                        {{ shippingMethod }}
                    </div>
                    <div v-if="!d2c" class="paragraph-default">
                        {{ freightPaymentTermComputed }} {{ collectAccountNo ? `- ${collectAccountNo}` : '' }}
                    </div>
                    <a v-if="trackingNumber && isSuccess" target="_blank" :href="trackingUrl" class="paragraph-default">
                        {{ trackingNumber }}
                    </a>
                    <div v-else-if="!trackingNumber && isSuccess" class="paragraph-default">No tracking number set yet. Please check back later.</div>
                </div>
            </div>
            <div  v-if="!d2c ? true : !['', null, undefined].includes(form.shipping_comments)" class="row">
                <div class="title-s title">
                    Options
                </div>
                <div class="text-rothcoText">
                    <div  v-if="!d2c" class="paragraph-default pb-25">
                        Drop Ship: {{ shipping.drop_ship == 0 ? 'No' : "Yes" }}
                    </div>
                    <div  v-if="!d2c" class="paragraph-default pb-25">
                        Ship Blind: {{ shipping.ship_blind == 0 ? 'No' : "Yes" }}
                    </div>
                    <form  v-if="!d2c" class="ship-edit pb-75" @submit.prevent="()=> {editShippingMustShipDate(); updateShippingField('shipping_must_ship_date');}">
                        <div v-if="shippingMustShipDateToggle" class="input-folder input-date">
                            <date-picker name="shipping_must_ship_date" v-model="form.shipping_must_ship_date_new" :min-date="props.mustShip.can_ship_today ? new Date() : new Date().setDate(new Date().getDate() + 1)" label="Must Ship On" :disabled-week-days="[0, 6]" />
                            <p v-if="allowEdit && form.errors.shipping_must_ship_date_new" class="input-support-text invalid-state">
                                {{ form.errors.shipping_must_ship_date_new }}
                            </p>
                        </div>
                        <div v-else class="paragraph-default input-folder">
                            Must Ship On: {{ form.shipping_must_ship_date_new ? moment(form.shipping_must_ship_date_new).format('MM/DD/YY') : 'No Date Selected' }}
                            <p v-if="allowEdit && form.errors.shipping_must_ship_date_new" class="input-support-text invalid-state">
                                {{ form.errors.shipping_must_ship_date_new }}
                            </p>
                        </div>
                        <button v-if="shippingMustShipDateToggle" type="submit" :class="{'primary':true, 'disabled': form.errors.shipping_must_ship_date_new}" :disabled="form.errors.shipping_must_ship_date_new || form.processing">Save</button>
                        <a v-else-if="allowEdit" href="#" @click.prevent="editShippingMustShipDate()" class="text-rothcoRed">{{ form.shipping_must_ship_date_new ? 'Edit' : 'Add' }}</a>
                    </form>
                    <div class="paragraph-default">
                        <form v-if="shippingCommentsToggle" @submit="()=>{editShippingComments(); updateShippingField('shipping_comments')}">
                            <div class="textarea-folder">
                                <PrimeTextArea 
                                    v-model="form.shipping_comments"
                                    id="shipping_comments"
                                    name="shipping_comments"
                                    placeholder=" " 
                                    class="input-folder--input">
                                </PrimeTextArea>
                                <label class="paragraph-default" for="shipping_comments">Shipping Comments</label>
                            </div>
                            <button :disabled="form.processing" type="submit" class="primary">Save</button>
                        </form>
                        <span v-else class="paragraph-default">
                            Shipping Comments: {{ form.shipping_comments }} 
                            <a v-if="allowEdit" href="#" @click.prevent="editShippingComments()" class="text-rothcoRed">{{ form.shipping_comments ? 'Edit' : 'Add' }}</a>
                        </span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { ref, onMounted, computed, inject, nextTick } from "vue";
    // import { route } from "ziggy-js";
    import { useForm } from "@/helpers/composables/useForm";
    import { string, object, date } from "yup";
    import { Link, usePage } from "@inertiajs/vue3";
    import moment from "moment";
    import DatePicker from "./DatePicker.vue";
    let shipping = defineModel({
        type: Object,
        default() {
            return {
                shipping_dropship: "",
                shipping_ship_blind: "",
                shipping_name: "",
                shipping_in_care_of: "",
                shipping_street1: "",
                shipping_street2: "",
                shipping_city: "",
                shipping_state: "",
                shipping_country: "",
                shipping_final_destination_state: "",
                shipping_final_destination_country: "",
                shipping_zip: "",
                shipping_phone: "",
                shipping_email: "",
                shipping_must_ship_date: new Date(),
                shipping_comments: "",
                shipping_method: "",
                tracking_url: null,
                tracking_number: null,
            };
        }
    });

    let props = defineProps({
        allowEdit: {
            type: Boolean,
            default: false
        },
        mustShip: {
            type: Object,
            default: () => {
                return {};
            }
        },
        isSuccess: {
            type: Boolean,
            default: false
        },
        d2c: {
            type: Boolean,
            default: false
        }
    });

    let route = inject("route");
    let shippingCommentsToggle = ref(false);

    const editShippingComments = () => {
        shippingCommentsToggle.value = !shippingCommentsToggle.value;
    };

    let shippingMustShipDateToggle = ref(false);

    const editShippingMustShipDate = () => {
        if(form.errors?.shipping_must_ship_date_new && shippingMustShipDateToggle.value) return;
        
        form.shipping_must_ship_date = form.shipping_must_ship_date_new;
        shippingMustShipDateToggle.value = !shippingMustShipDateToggle.value;
    };

    const serviceLevel = computed(()=>{
        return shipping.value.serviceLevel?.name;
    })

    const shippingMethod = computed(()=>{
        return shipping.value.shippingMethod?.name || shipping.value.shipping_method;
    })

    const collectAccountNo = computed(()=>{
        return shipping.value.collectAccount?.collect_no;
    })
    
    const trackingUrl = computed(()=>{
        return shipping.value.tracking_url;
    });

    const trackingNumber = computed(()=>{
        return shipping.value.tracking_number;
    })

    const finalDestinationString = computed(()=>{
        if(['RSUS','RSTR'].includes(shipping.value.shipping_final_destination_country)){
            return "Multiple Countries Outside USA";
        }else{
            return `${shipping.value.shipping_final_destination_state ? `${shipping.value.shipping_final_destination_state}, ` : 'Multiple States/Regions, '}${shipping.value.shipping_final_destination_country}`;
        }
    })

    const freightPaymentTermComputed = computed(()=>{
        if(shipping.value.freight_payment_term == 'Prepaid'){
            return 'BillWithShipment'
        }else{
            return shipping.value.freight_payment_term;
        }
    })

    const form = useForm({
        useInertia: false,
        initialErrors: Object.keys(usePage().props?.errors ?? {}).reduce(
            (acc, key) => {
                if(key == 'shipping_must_ship_date'){
                    acc.shipping_must_ship_date_new = usePage().props?.errors[key];
                    shippingMustShipDateToggle.value = true;
                }   
                return acc;
            },
            {}
        ),
        schema: object({
            // SHIPPING
            shipping_comments: string().notRequired().default(shipping.value.shipping_comments),
            shipping_must_ship_date: date().notRequired()
                .test("shipping_must_ship_date", "", (value, ctx) => {
                    if (!value) return true
                    let isMustShipToday = moment(value)
                        .startOf("day")
                        .isSame(moment(new Date()).startOf("day"));
                    if (isMustShipToday) {
                        if (!props.mustShip.can_ship_today) {
                            return ctx.createError({
                                message:
                                    "Must Ship Date invalid - cannot ship today"
                            });
                        }
                    }else {
                        if(moment(value).startOf("day").isBefore(moment(new Date()).startOf("day"))) {
                            return ctx.createError({
                                message:
                                    "Must Ship Date invalid - cannot be earlier than today"
                            });
                        }else if([0,6].includes(moment(value).day())){
                            return ctx.createError({
                                message:"Must Ship Date invalid - cannot select a weekend date"
                            });
                        }
                    }
                    return true;
                })
                .default(() => {
                    return shipping.value.shipping_must_ship_date ? moment(shipping.value.shipping_must_ship_date).toDate() : null;
                }),
            shipping_must_ship_date_new: date().notRequired()
                .test("shipping_must_ship_date_new", "", (value, ctx) => {
                    if (!value) return true
                    let isMustShipToday = moment(value)
                        .startOf("day")
                        .isSame(moment(new Date()).startOf("day"));
                    if (isMustShipToday) {
                        if (!props.mustShip.can_ship_today) {
                            return ctx.createError({
                                message:
                                    "Must Ship Date invalid - cannot ship today"
                            });
                        }
                    }else {
                        if(moment(value).startOf("day").isBefore(moment(new Date()).startOf("day"))) {
                            return ctx.createError({
                                message:
                                    "Must Ship Date invalid - cannot be earlier than today"
                            });
                        }else if([0,6].includes(moment(value).day())){
                            return ctx.createError({
                                message:"Must Ship Date invalid - cannot select a weekend date"
                            });
                        }
                    }
                    return true;
                })
                .default(() => {
                    return shipping.value.shipping_must_ship_date ? moment(shipping.value.shipping_must_ship_date).toDate() : null;
                }),
        })
    })

    const updateShippingField = async (field) => {
        if(form.processing) return;
        await form.validate({all: true})
        nextTick(()=>{
            if (!form.errors[field]) {
                form.post(route("checkout.save-review"), {
                    [field]: field == 'shipping_must_ship_date' ? form[field] ? moment(form[field]).format('YYYY-MM-DD') : null : form[field]
                });
                shipping.value[field] = form[field];
            }
        })
    };

    const editingFields = computed(()=>{
        if(!props.allowEdit || form.hasErrors) return {};
        // used to prevent edited + unsaved fields from being lost when placing an order 
        let fields = {};
        if(shippingMustShipDateToggle.value){ // is editing must ship date
            fields['shipping_must_ship_date'] = form.shipping_must_ship_date_new ? moment(form.shipping_must_ship_date_new).format('YYYY-MM-DD') : null
        }
        if(shippingCommentsToggle.value){ // is editing shipping comments
            fields['shipping_comments'] = form.shipping_comments;
        }
        return fields;
    })

    const formErrors = computed(()=>{
        return form.errors;
    });

    const validateForm = ()=>{
        form.validate({all: true});
    }

    onMounted(()=>{
        validateForm();
    })

    defineExpose({formErrors, validate: validateForm, editingFields})
</script>
